import React, { useState, useEffect, useRef } from 'react';
import { useThread } from './ThreadContext';
import ChatContainer from './ChatContainer';
import ChatInput from './ChatInput';
import BouncingDotsLoader from './BouncingDotsLoader';

interface SearchEvent {
    completed: boolean;
    results: string[];
    search_message: string;
}

//const BASE_URL = "https://reprompt--luzern-api-fastapi-app-dev.modal.run"
const BASE_URL = "https://reprompt--khr-api-fastapi-app.modal.run"
const ChatComponent: React.FC = () => {
    const { threadId, setThreadId } = useThread();
    const [messages, setMessages] = useState<{ id: string, role: string, content: string, searchEvent: SearchEvent | null }[]>([]);
    const [input, setInput] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [currentMessage, setCurrentMessage] = useState<string>('');
    const [currentSearchEvent, setCurrentSearchEvent] = useState<SearchEvent | null>(null);

    useEffect(() => {
        if (!threadId) {
            createThread();
        }
    }, [threadId]);

    const createThread = async () => {
        const response = await fetch(BASE_URL + '/api/create-thread', { method: 'POST' });
        const data = await response.json();
        setThreadId(data.threadId);
    };

    const sendMessage = async () => {
        if (!input.trim()) return;

        setIsSending(true);

        const newMessage = { id: `${Date.now()}`, role: 'user', content: input, searchEvent: null };
        setMessages([...messages, newMessage]);
        setInput('');

        const response = await fetch(BASE_URL + '/api/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: input, threadId }),
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let done = false;
        let buffer = '';
        let accumulatedText = '';

        while (!done) {
            const { value, done: doneReading } = await reader?.read()!;
            done = doneReading;
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split('\n');
            buffer = lines.pop()!; // Keep the last partial line in the buffer

            for (const line of lines) {
                if (line.trim()) {
                    const parsed = JSON.parse(line);
                    if (parsed.type === 'text_delta') {
                        accumulatedText += parsed.data;
                        setCurrentMessage(accumulatedText);
                    } else if (parsed.type === 'search_started') {
                        setCurrentSearchEvent(parsed.data)
                    } else if (parsed.type === 'search_completed') {
                        setCurrentSearchEvent(parsed.data)
                    }
                }
            }
            
            setIsSending(false);

            if (done) {
                setMessages((prevMessages) => [
                    ...prevMessages,
                    { id: `${Date.now()}`, role: 'assistant', content: accumulatedText, searchEvent: currentSearchEvent },
                ]);
                setCurrentMessage('');
                setCurrentSearchEvent(null);
            }
        }
    };

    return (
        <div className="fixed inset-0 inset-auto bottom-16 absolute top-auto right-0 mr-4 bg-white p-6 rounded-lg border border-gray-200 flex flex-col w-[95%] max-w-full h-auto shadow-md">
            <div className="flex-none flex items-center justify-between">
                <div className="flex justify-between items-center w-full">
                    <div className="flex items-center p-4 bg-white rounded-lg">
                        <img src="https://s3-eu-west-1.amazonaws.com/yousty-switzerland/company_profiles/831866/logo/73c4cc62986bc48504b7384d8ac44372/default_1594106376_702dc3acff3937c789ef524970334198.jpg" alt="Logo" className="h-12 mr-2" />
                        {/*<h1 className="text-xl font-semibold">Chat</h1>*/}
                    </div>
                </div>
            </div>
            <div className="overflow-auto mb-4">
                <ChatContainer messages={messages} currentMessage={currentMessage} currentSearchEvent={currentSearchEvent} />
            </div>
            <BouncingDotsLoader visible={isSending} />
            <ChatInput input={input} setInput={setInput} sendMessage={sendMessage} />
        </div>
    );
};

export default ChatComponent;
